import { Directive } from '@angular/core';
import {
    CraftService,
    DO_NOTHING,
    FlightLogFileDto,
    FlightLogsService,
    FlightLogSummaryDto,
    FlyFreelyError,
    FlyFreelyLoggingService,
    hasAnyPermission,
    MissionService,
    PersonsOrganisationDto,
    RpaDto,
    toLookup,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { WorkspaceStateService } from '@flyfreely-portal-ui/workspace';
import { Angulartics2 } from 'angulartics2';
import { FlightLogDataService } from 'libs/flight-logs/src/lib/flight-log-data.service';
import { combineLatest, from, of, ReplaySubject, Subject } from 'rxjs';
import {
    concatMap,
    filter,
    map,
    mergeMap,
    startWith,
    switchMap,
    take,
    takeUntil,
    tap
} from 'rxjs/operators';
import { MissionRecordService } from '../mission-record-edit/mission-record.service';
import { MissionCompletionService } from './mission-completion.service';
import { MissionCompletionOrRecordService } from './unassigned-mission-flight-logs/interfaces';

export interface FlightLogFileWithRpa extends FlightLogFileDto {
    rpa: RpaDto;
    startTime?: string;
    summary?: FlightLogSummaryDto;
}

@Directive({
    selector: '[missionFlightLogs]',
    providers: [FlightLogDataService]
})
export class MissionFlightLogsDirective {
    state$ = new ReplaySubject<{
        missionId: number;
        organisationId: number;
        canViewOrganisation: boolean;
    }>(1);

    missionDataService?: MissionCompletionOrRecordService;

    private rpaSource = new ReplaySubject<RpaDto[]>();
    private flightLogsSource = new ReplaySubject<FlightLogFileDto[]>();

    private flightLogWithRpaSource = new ReplaySubject<FlightLogFileWithRpa[]>(
        1
    );
    private workingSource = new ReplaySubject<boolean>(1);

    flightLogs$ = this.flightLogWithRpaSource.asObservable();
    working$ = this.workingSource.asObservable();

    private workTracker = new WorkTracker();
    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private missionCompletionService: MissionCompletionService,
        private missionRecordService: MissionRecordService,
        private missionService: MissionService,
        private flightLogService: FlightLogsService,
        private flightLogDataService: FlightLogDataService,
        private workspaceStateService: WorkspaceStateService,
        private logging: FlyFreelyLoggingService,
        private craftService: CraftService,
        private angulartics2: Angulartics2
    ) {}

    ngOnInit() {
        combineLatest([
            this.workTracker.observable,
            this.flightLogDataService.working$
        ])
            .pipe(startWith([false, false]), takeUntil(this.ngUnsubscribe$))
            .subscribe(([working, loading]) => {
                this.workingSource.next(working || loading);
            });

        combineLatest([this.flightLogsSource, this.rpaSource])
            .pipe(
                map(([logs, rpas]) => {
                    const rpaLookup = rpas.reduce(toLookup, {});

                    return logs.map(
                        l =>
                            ({
                                ...l,
                                rpa: rpaLookup[l.rpaId]
                            } as FlightLogFileWithRpa)
                    );
                })
            )
            .subscribe(logfiles => this.flightLogWithRpaSource.next(logfiles));
        this.refreshFlightLogs();

        this.flightLogService.change$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.refreshFlightLogs());

        this.missionCompletionService.savedMission$
            .pipe(
                mergeMap(mission =>
                    this.workspaceStateService.currentLoadedOrganisation$.pipe(
                        map(organisation => [mission, organisation] as const)
                    )
                ),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(([mission, organisation]) => {
                this.state$.next({
                    organisationId: mission.organisationId,
                    missionId: mission.id,
                    canViewOrganisation: hasAnyPermission(
                        organisation.organisation,
                        [PersonsOrganisationDto.Permissions.ORGANISATION_VIEW]
                    )
                });
            });
        /* this.missionCompletionService.savedMission$
            .pipe(
                mergeMap(mission => merge([of(mission), this.workspaceStateService.currentLoadedOrganisation$])),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(([mission, organisation]) => {
                this.state$.next({
                    organisationId: mission.organisationId,
                    missionId: mission.id,
                });
            }); */
        this.missionRecordService.savedMission$
            .pipe(
                mergeMap(mission =>
                    this.workspaceStateService.currentLoadedOrganisation$.pipe(
                        map(organisation => [mission, organisation] as const)
                    )
                ),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(([mission, organisation]) => {
                this.state$.next({
                    organisationId: mission.organisationId,
                    missionId: mission.id,
                    canViewOrganisation: hasAnyPermission(
                        organisation.organisation,
                        [PersonsOrganisationDto.Permissions.ORGANISATION_VIEW]
                    )
                });
            });

        this.state$
            .pipe(
                filter(state => state.canViewOrganisation),
                mergeMap(({ organisationId }) =>
                    this.craftService.findCrafts(organisationId)
                )
            )
            .subscribe(rpas => {
                this.rpaSource.next(rpas);
            });

        this.flightLogDataService.flightLogs$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(flightlogs => {
                this.flightLogsSource.next(flightlogs);
            });
    }

    ngOnDestroy() {
        this.flightLogsSource.complete();
        this.flightLogWithRpaSource.complete();
        this.rpaSource.complete();
        this.workingSource.complete();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    setServiceType(historicalMission: boolean) {
        if (historicalMission) {
            this.missionDataService = this.missionRecordService;
        } else {
            this.missionDataService = this.missionCompletionService;
        }
    }

    refreshFlightLogs() {
        this.state$
            .pipe(
                filter(state => state.canViewOrganisation),
                tap(({ organisationId, missionId }) =>
                    this.flightLogDataService.findFlightLogs(
                        0,
                        // Can't do pagination on this.
                        1000000,
                        null,
                        {
                            organisationId: organisationId,
                            missionId: missionId,
                            flightLogCollectionSourceIdList: null,
                            uploadedByIdList: null
                        }
                    )
                ),
                take(1),
                takeUntil(this.ngUnsubscribe$)
            )
            // Don't need to do anything since the data service observable will trigger the rest.
            .subscribe(DO_NOTHING);
    }

    assignFlightLog(flightLogId: number, flightId?: number) {
        const doneWorking = this.workTracker.createTracker();
        return this.doAssignFlightLog(flightLogId, flightId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: _ => {
                    doneWorking();
                    this.missionService.notifyMissionChanged();
                    this.refreshFlightLogs();
                    this.logging.success('Flight log assigned');
                },
                error: (error: FlyFreelyError) => {
                    // return the log to the unassigned list
                    this.unassignFlightLog(flightLogId);
                    doneWorking();
                    this.logging.error(
                        error,
                        `Error assigning flight log: ${error.message}`
                    );
                }
            });
    }

    assignFlightLogs(assignCommands: { logId: number; flightId: number }[]) {
        from(assignCommands)
            .pipe(
                concatMap(command =>
                    this.doAssignFlightLog(
                        command.logId,
                        command.flightId
                    ).pipe(
                        tap({
                            // return the log to the unassigned list
                            error: () => this.unassignFlightLog(command.logId)
                        })
                    )
                )
            )
            .subscribe({
                next: _ => {
                    this.refreshFlightLogs();
                    this.logging.success('Flight log assigned');
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error assigning flight log: ${error.message}`
                    );
                }
            });
    }

    /**
     * Perform the assignment of the flight log to a flight.
     * @param flightLogId the flight log to assign
     * @param flightId the flight log to assign to
     * @returns the observable of the action
     */
    doAssignFlightLog(flightLogId: number, flightId?: number) {
        return this.state$.pipe(
            mergeMap(({ missionId }) =>
                this.flightLogService.assignFlightLog(flightLogId, {
                    flightId: flightId,
                    missionId
                })
            ),
            take(1),
            takeUntil(this.ngUnsubscribe$),
            tap(result => {
                this.missionDataService!.updateFlight(result.flight);
            })
        );
    }

    unassignFlightLog(flightLogId: number) {
        const doneWorking = this.workTracker.createTracker();
        this.state$
            .pipe(
                mergeMap(({ missionId }) =>
                    this.flightLogService.assignFlightLog(flightLogId, {
                        flightId: null,
                        missionId: missionId
                    })
                ),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe({
                next: result => {
                    this.missionDataService!.updateFlight(result.flight);
                    this.missionService.notifyMissionChanged();
                    this.refreshFlightLogs();
                    doneWorking();
                    this.logging.success('Flight log unassigned');
                },
                error: (error: FlyFreelyError) => {
                    doneWorking();
                    this.logging.error(
                        error,
                        `Error unassigning flight log: ${error.message}`
                    );
                }
            });
    }

    unattachFlightLog(flightLogId: number) {
        const doneWorking = this.workTracker.createTracker();
        this.flightLogService
            .assignFlightLog(flightLogId, {
                flightId: null,
                missionId: null
            })
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: () => {
                    this.refreshFlightLogs();
                    doneWorking();
                    this.logging.success('Flight log unattached');
                },
                error: (error: FlyFreelyError) => {
                    doneWorking();
                    this.logging.error(
                        error,
                        `Error unattaching flight log: ${error.message}`
                    );
                }
            });
    }

    createFlightsFromLogs(
        flightLogIdList: number[],
        pilotId: number | undefined
    ) {
        if (flightLogIdList.length === 0) {
            return of([]);
        }

        const doneWorking = this.workTracker.createTracker();

        return this.state$.pipe(
            switchMap(state =>
                this.flightLogService.createFlightsFromLogs({
                    missionId: state.missionId,
                    pilotId,
                    flightLogIdList
                })
            ),
            tap({ complete: doneWorking, error: doneWorking })
        );
    }
}
